// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Onboarding / Registration
// Create or link organization when biz_owner has no org match
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Button, TextInput, Select,
  SimpleGrid, Stack, ThemeIcon, Stepper, Divider,
} from '../../../components/gab/GabComponents';
import { IconBuilding, IconCheck, IconArrowRight } from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useAuthStore } from '../../../store/authStore';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import { SECTOR_TYPES } from '../constants';

const SECTOR_SELECT = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));

export default function PortalOnboarding() {
  const user = useAuthStore(s => s.user);
  const organizations = useMainStreetStore(s => s.organizations);
  const createOrganization = useMainStreetStore(s => s.createOrganization);
  const loadOrganizations = useMainStreetStore(s => s.loadOrganizations);

  const [mode, setMode] = useState<'choose' | 'create' | 'link'>('choose');
  const [saving, setSaving] = useState(false);

  // Create form
  const [form, setForm] = useState({
    name: '', sectorType: '', address: '', city: '', state: '', zip: '',
    phone: '', primaryContact: user?.fullName ?? '',
  });

  // Link form
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const setField = (key: string, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const orgOptions = useMemo(() =>
    organizations
      .filter(o => !o.email) // Only show orgs not already claimed
      .map(o => ({ value: String(o.id), label: o.name || `Org #${o.id}` })),
    [organizations],
  );

  // ── Create new org ──────────────────────────────────────────

  async function handleCreate() {
    if (!form.name.trim()) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Business name is required.' });
      return;
    }
    setSaving(true);
    try {
      const org = await createOrganization({
        name: form.name.trim(),
        sectorType: form.sectorType,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        phone: form.phone.trim(),
        email: user?.email ?? '',
        primaryContact: form.primaryContact.trim(),
        opStatus: 'operational',
        insurance: 'unknown',
        continuity: 'unknown',
        lastVerified: new Date().toISOString(),
        notes: '',
        coordinates: '',
      });
      if (org) {
        notifications.show({ color: 'var(--color-success)', title: 'Registered', message: `${org.name} has been registered.` });
        await loadOrganizations();
      }
    } catch (err: unknown) {
      notifications.show({ color: 'var(--color-error)', title: 'Error', message: err instanceof Error ? err.message : 'Registration failed' });
    } finally {
      setSaving(false);
    }
  }

  // ── Link existing org ───────────────────────────────────────

  async function handleLink() {
    if (!selectedOrgId) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Select an organization.' });
      return;
    }
    const org = organizations.find(o => String(o.id) === selectedOrgId);
    if (!org) return;

    setSaving(true);
    try {
      const updateOrganization = useMainStreetStore.getState().updateOrganization;
      await updateOrganization(org.id, {
        email: user?.email ?? '',
        primaryContact: org.primaryContact || (user?.fullName ?? ''),
      });
      notifications.show({ color: 'var(--color-success)', title: 'Linked', message: `Linked to ${org.name}.` });
      await loadOrganizations();
    } catch (err: unknown) {
      notifications.show({ color: 'var(--color-error)', title: 'Error', message: err instanceof Error ? err.message : 'Link failed' });
    } finally {
      setSaving(false);
    }
  }

  // ── Choose mode ─────────────────────────────────────────────

  if (mode === 'choose') {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <ThemeIcon size={64} radius="xl" variant="light" color="var(--color-primary-700)" mb="md">
              <IconBuilding size={32} />
            </ThemeIcon>
            <Title order={2} mb="xs">Welcome to MainStreet</Title>
            <Text c="dimmed" size="sm">
              The Business Owner Portal lets you manage your organization's profile,
              facilities, contacts, and coordinate with the EOC during incidents.
            </Text>
          </div>

          <Stepper active={0} size="sm" mb="xl">
            <Stepper.Step label="Register" description="Set up your business" />
            <Stepper.Step label="Complete Profile" description="Add details" />
            <Stepper.Step label="Ready" description="Coordinate with EOC" />
          </Stepper>

          <Stack gap="md">
            <Paper withBorder p="lg" style={{ cursor: 'pointer' }}
              onClick={() => setMode('create')}>
              <Group>
                <ThemeIcon size="lg" variant="light" color="var(--color-primary-700)">
                  <IconBuilding size={20} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text fw={600}>Register New Business</Text>
                  <Text size="xs" c="dimmed">
                    Create a new organization profile for your business
                  </Text>
                </div>
                <IconArrowRight size={18} color="#939598" />
              </Group>
            </Paper>

            {orgOptions.length > 0 && (
              <Paper withBorder p="lg" style={{ cursor: 'pointer' }}
                onClick={() => setMode('link')}>
                <Group>
                  <ThemeIcon size="lg" variant="light" color="var(--data-viz-650)">
                    <IconCheck size={20} />
                  </ThemeIcon>
                  <div style={{ flex: 1 }}>
                    <Text fw={600}>Link Existing Organization</Text>
                    <Text size="xs" c="dimmed">
                      Your business is already registered — claim it
                    </Text>
                  </div>
                  <IconArrowRight size={18} color="#939598" />
                </Group>
              </Paper>
            )}
          </Stack>

          <Text size="xs" c="dimmed" ta="center" mt="xl">
            Signed in as {user?.email}
          </Text>
        </div>
      </div>
    );
  }

  // ── Create form ─────────────────────────────────────────────

  if (mode === 'create') {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <Group mb="lg">
            <Button variant="subtle" size="xs" onClick={() => setMode('choose')}>Back</Button>
            <Title order={3}>Register Your Business</Title>
          </Group>

          <Paper withBorder p="lg">
            <Text size="sm" c="dimmed" mb="md">
              Fill in your business details. You can update these later from your portal dashboard.
              Your account ({user?.email}) will be automatically linked to this organization.
            </Text>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Business Name" required value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder="e.g. Smith Hardware" />
              <Select label="Sector" data={SECTOR_SELECT} value={form.sectorType || null}
                onChange={v => setField('sectorType', v ?? '')}
                placeholder="Select your industry" searchable clearable />
              <TextInput label="Address" value={form.address}
                onChange={e => setField('address', e.target.value)} />
              <TextInput label="City" value={form.city}
                onChange={e => setField('city', e.target.value)} />
              <TextInput label="State" value={form.state}
                onChange={e => setField('state', e.target.value)} />
              <TextInput label="ZIP" value={form.zip}
                onChange={e => setField('zip', e.target.value)} />
              <TextInput label="Phone" value={form.phone}
                onChange={e => setField('phone', e.target.value)} />
              <TextInput label="Primary Contact Name" value={form.primaryContact}
                onChange={e => setField('primaryContact', e.target.value)} />
            </SimpleGrid>

            <Divider my="md" />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setMode('choose')}>Cancel</Button>
              <Button loading={saving} onClick={handleCreate}
                leftSection={<IconBuilding size={16} />}>
                Register Business
              </Button>
            </Group>
          </Paper>
        </div>
      </div>
    );
  }

  // ── Link form ───────────────────────────────────────────────

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <Group mb="lg">
          <Button variant="subtle" size="xs" onClick={() => setMode('choose')}>Back</Button>
          <Title order={3}>Link Your Organization</Title>
        </Group>

        <Paper withBorder p="lg">
          <Text size="sm" c="dimmed" mb="md">
            Select your organization from the list below. This will link your account
            ({user?.email}) as the primary contact.
          </Text>

          <Select
            label="Organization"
            data={orgOptions}
            value={selectedOrgId}
            onChange={setSelectedOrgId}
            placeholder="Search for your organization..."
            searchable
            size="sm"
            mb="md"
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setMode('choose')}>Cancel</Button>
            <Button loading={saving} onClick={handleLink}
              leftSection={<IconCheck size={16} />}>
              Link Organization
            </Button>
          </Group>
        </Paper>
      </div>
    </div>
  );
}
